import { useState, useCallback } from "react";
import CardSubtitle from "reactstrap/lib/CardSubtitle";
import { Card, Row, Col, CardTitle, Button, Badge, Input } from "reactstrap";
import { CRow, CCol, CForm, CFormLabel, CFormInput } from "@coreui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  HandleUserNameChange,
  HandleUserEmailChange,
  CheckIfEmailIsOccupied,
  HandleClassEdit,
  HandleAddNewTeacher,
  HandleDeleteStudent,
  SaveClassFocusControlSetting,
  DeleteClassFocusControlSetting,
} from "../../actions/teacherMutations";
import ConfirmDialog from "../Modal/ConfirmDialog";
import { toast } from "react-toastify";
import { FormattedMessage } from "react-intl";
import Select from "react-select";
import { PacmanLoader } from "react-spinners";
import DataGrid from "../DataGrids/DataGrid";
import DisconnectTeacherFromClassButton from "../Button/DisconnectTeacherFromClassButton";
import AddNewTeacherToClass from "../Modal/AddNewTeacherToClass";
import StudentLicenseManagemenModal from "../Modal/StudentLicenseManagemenModal";
import FinalExamUnlockerContainer from "./FinalExamUnlockerContainer";
import { v4 as uuid } from "uuid";
import CreateStudentInClass from "../Modal/CreateStudentInClass";
import UpdateClassModal from "../Modal/UpdateClassModal";
import MoveExistingStudentToClassModal from "../Modal/MoveExistingStudentToClassModal";
import ImportStudentsToClassModal from "../Modal/ImportStudentsToClassModal";
import ButtonSendActivationMail from "../Button/ButtonSendActivationMail";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { checkNameLengthAndCharacters } from "../../Utilities/checkerFunctions";
import ModuleLocker from "./ModuleLocker";
import { GetCoursesByClassId } from "../../actions/teacherQueries";
import { formatDate } from "../../Utilities/dateUtils";
import { PiCheckCircle } from "react-icons/pi";
import FocusControl from "../Teacher/FocusControl";
import useFocusControl from "./hooks/useFocusControl";

export default function ClassAdministration(props) {
  const classCoursesData = GetCoursesByClassId(props.classData.data.id);

  const [teacherForRemoval, setTeacherForRemoval] = useState("");
  const [removingTeacher, setRemovingTeacher] = useState(false);
  const [promptTeacherRemoval, setPromptTeacherRemoval] = useState(false);
  const [studentForRemoval, setStudentForRemoval] = useState("");
  const [promptStudentRemoval, setPromptStudentRemoval] = useState(false);
  const [updateClassModal, setUpdateClassModal] = useState(false);
  const [addNewStudentToClassModal, setAddNewStudentToClassModal] =
    useState(false);
  const [addStudentToClassModal, setAddStudentToClassModal] = useState(false);
  const [licenseModalOpen, setLicenseModalOpen] = useState(false);
  const [selectedStudentForLicense, setSelectedStudentForLicense] =
    useState(null);
  const [addNewTeacherModalOpen, setAddNewTeacherModalOpen] = useState(false);
  const [updatingClass, setUpdatingClass] = useState(false);
  const [showCourses, setShowCourses] = useState(false);
  const [importStudentsModalOpen, setImportStudentsModalOpen] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [classNameInvalidSigns, setClassNameInvalidSigns] = useState(false);
  const [studentFilterSearch, setStudentFilterSearch] = useState("");
  const [teacherFilterSearch, setTeacherFilterSearch] = useState("");

  const mutateStudentName = useMutation({ mutationFn: HandleUserNameChange });
  const mutateStudentEmail = useMutation({ mutationFn: HandleUserEmailChange });
  const mutateAddNewTeacher = useMutation({ mutationFn: HandleAddNewTeacher });
  const mutateCheckStudentEmail = useMutation({ mutationFn: CheckIfEmailIsOccupied });
  const mutateDeleteStudent = useMutation({ mutationFn: HandleDeleteStudent });
  const mutateEditClass = useMutation({ mutationFn: HandleClassEdit });
  const queryClient = useQueryClient();

  const buildDeviantData = useCallback(
    (fcSettings) => {
      const moduleItem = fcSettings.find((x) => x.classSetting?.target === 0);
      const finalItem = fcSettings.find((x) => x.classSetting?.target === 1);
      const allDeviantStudents = [
        ...(moduleItem?.deviantStudentSettings || []),
        ...(finalItem?.deviantStudentSettings || []),
      ];
      const uniqueDeviantStudentIds = [
        ...new Set(allDeviantStudents.map((s) => s.studentId)),
      ];
      const students = props.classData.data.students || [];
      const deviantStudents = uniqueDeviantStudentIds.map((studentId) => {
        const student = students.find((s) => s.id === studentId);
        return {
          studentId,
          studentName: student?.name || null,
          className: props.classData.data.name,
        };
      });
      return { deviantClasses: [], deviantStudents };
    },
    [props.classData.data.students, props.classData.data.name],
  );

  const onRefreshSettings = useCallback(async () => {
    const classId = String(props.classData.data.id);
    await queryClient.refetchQueries({
      queryKey: ["FetchClassByClassId", classId],
      exact: true,
    });
    const updatedClass = queryClient.getQueryData([
      "FetchClassByClassId",
      classId,
    ]);
    return updatedClass?.focusControlSettings || [];
  }, [queryClient, props.classData.data.id]);

  const fc = useFocusControl({
    settingKey: "classSetting",
    entityId: props.classData.data.id,
    entityIdKey: "classId",
    focusControlSettings: props.classData.data.focusControlSettings,
    saveFn: SaveClassFocusControlSetting,
    deleteFn: DeleteClassFocusControlSetting,
    onRefreshSettings,
    buildDeviantData,
  });

  if (classCoursesData.isPending || !classCoursesData.data) {
    return (
      <div className="inline-pacman-loader">
        <PacmanLoader color="#20a8d8" size={20} />
      </div>
    );
  }

  let values = {
    id: props.classData.data.id,
    name: props.classData.data.name,
    school: props.teacherInfo.data.customer.name,
    programme: props.classData.data.programme,
    orientation: props.classData.data.orientation,
    teachers: props.classData.data.teachers,
  };

  const handleAddTeacherSelect = (event) => {
    if (event.value === "addNewTeacher") {
      setAddNewTeacherModalOpen(true);
    } else {
      handleAddTeacherToClass(event.value);
    }
  };

  function compare(a, b) {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
    return 0;
  }

  const promptRemoveTeacher = (teacher) => {
    setTeacherForRemoval(teacher);
    setPromptTeacherRemoval(true);
  };

  const promptRemoveStudent = (student) => {
    setStudentForRemoval(student);
    setPromptStudentRemoval(true);
  };

  const toggleImportStudentsModal = () => {
    setImportStudentsModalOpen((prev) => !prev);
  };

  const reload = () => {};

  const reloadClass = () => {
    queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });
    queryClient.invalidateQueries({ queryKey: ["FetchClassByClassId"] });
  };

  var allStudents = [];

  props.teacherInfo.data.programmes.forEach((prog) => {
    prog.classes.forEach((cl) => {
      cl.students.forEach((stud) => {
        allStudents.push(stud);
      });
    });
  });

  allStudents = allStudents.sort(compare);
  var studentsInClass = [];
  props.classData.data.students.forEach((stud) => {
    studentsInClass.push(stud);
  });

  const openAddNewStudentToClassModal = () => {
    toggleModals("addNewStudentToClass");
  };
  const openAddStudentToClassModal = () => {
    toggleModals("addStudentToClass");
  };

  const closeAddNewStudentAndAddLicense = (student) => {
    let newStudent = {
      id: student.id,
      name: student.name,
      email: student.email,
      class: {
        id: props.classData.data.id,
        name: props.classData.data.name,
        programme: props.classData.data.programme,
        orientation: props.classData.data.orientation,
      },
    };
    toggleModals("addNewStudentToClass");
    setSelectedStudentForLicense(newStudent);
    setLicenseModalOpen(true);
  };

  const toggleModals = (entity) => {
    switch (entity) {
      case "addNewStudentToClass":
        setAddNewStudentToClassModal(!addNewStudentToClassModal);
        break;
      case "addStudentToClass":
        setAddStudentToClassModal(!addStudentToClassModal);
        break;
      case "updateClass":
        setUpdateClassModal(!updateClassModal);
        break;
      default:
        this.setState({ studentModal: !this.state.studentModal });
    }
  };

  const handleAddNewTeacher = (values) => {
    setAddNewTeacherModalOpen(false);
    if (checkNameLengthAndCharacters(values.name) !== true) {
      toast.warn(checkNameLengthAndCharacters(values.name));
    } else {
      mutateAddNewTeacher.mutate(
        {
          name: values.name,
          email: values.email,
          fullAccess: values.fullAccess,
          purchaser: values.purchaser,
        },
        {
          onSuccess: (data) => {
            if (data && data.status > 199 && data.status < 300) {
              handleAddTeacherToClass(data.data.result);
            } else {
              toast.warn(data);
            }
          },
          throwOnError: true,
          onError: (data) => {
            toast.warn(data.response.data);
          },
        }
      );
    }
  };

  const handleAddTeacherToClass = (teacherToAdd) => {
    let teachers = values.teachers;
    teachers.push(teacherToAdd);
    mutateEditClass.mutate(
      {
        id: values.id,
        name: values.name,
        school: values.school,
        programme: values.programme,
        orientation: values.orientation,
        teachers: teachers,
      },
      {
        onSuccess: (data) => {
          if (data && data.status > 199 && data.status < 300) {
            queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });
            queryClient.invalidateQueries({ queryKey: ["FetchClassByClassId"] });
            setPromptTeacherRemoval(false);
            toast.success(<FormattedMessage id="ClassUpdated" />);
          } else {
            toast.warn(data);
          }
        },
        throwOnError: true,
        onError: (data) => {
          setPromptTeacherRemoval(false);
          toast.warn(data.response.data);
        },
      }
    );
  };

  const toggleNewTeacherModal = () => {
    setAddNewTeacherModalOpen((prevstate) => !prevstate);
  };

  const handleRemoveTeacher = async () => {
    setRemovingTeacher(true);
    setPromptTeacherRemoval(false);
    let tempClassData = props.classData.data;
    var teachers = props.classData.data.teachers.filter(function (teacher) {
      return teacher.id !== teacherForRemoval.id;
    });
    tempClassData.teachers = teachers;
    queryClient.setQueryData(
      ["FetchClassByClassId", props.classData.data.id],
      tempClassData
    );
    await mutateEditClass.mutate(
      {
        id: values.id,
        name: values.name,
        school: values.school,
        programme: values.programme,
        orientation: values.orientation,
        teachers: teachers,
      },
      {
        onSuccess: (data) => {
          setRemovingTeacher(false);
          if (data && data.status > 199 && data.status < 300) {
            queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });
            toast.success(<FormattedMessage id="ClassUpdated" />);
          } else {
            tempClassData.teachers.push(teacherForRemoval);
            queryClient.setQueryData(
              ["FetchClassByClassId", props.classData.data.id],
              tempClassData
            );
            toast.warn(data);
          }
        },
        throwOnError: true,
        onError: (data) => {
          tempClassData.teachers.push(teacherForRemoval);
          queryClient.setQueryData(
            ["FetchClassByClassId", props.classData.data.id],
            tempClassData
          );
          setRemovingTeacher(false);
          setPromptTeacherRemoval(false);
          toast.warn(data.response.data);
        },
      }
    );
  };

  const handleDeleteStudent = () => {
    setPromptStudentRemoval(false);
    setUpdatingClass(true);
    mutateDeleteStudent.mutate(
      {
        studentEmail: studentForRemoval.email,
      },
      {
        onSuccess: (data) => {
          if (data && data.status > 199 && data.status < 300) {
            queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });
            queryClient.invalidateQueries({ queryKey: ["FetchClassByClassId"] });
            setUpdatingClass(false);
            toast.success(<FormattedMessage id="StudentDeleted" />);
          } else {
            toast.warn(data);
          }
        },
        throwOnError: true,
        onError: (data) => {
          setUpdatingClass(false);
          toast.warn(data.response.data);
        },
      }
    );
  };

  const handleChangedClassName = (event) => {
    if (
      event.target.name === "className" &&
      event.target.value !== props.classData.data.name
    ) {
      if (!event.target.value.match(/^[A-Za-z0-9,\-,",Å,å,Ä,ä,Ö,ö ]+$/)) {
        setClassNameInvalidSigns(true);
        return null;
      }
      setClassNameInvalidSigns(false);
      setUpdatingClass(true);
      values.newName = event.target.value;
      mutateEditClass.mutate(
        {
          id: props.classData.data.id,
          name: values.newName,
          school: values.school,
          programme: values.programme,
          orientation: values.orientation,
          teachers: values.teachers,
        },
        {
          onSuccess: (data) => {
            if (data && data.status > 199 && data.status < 300) {
              queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });
              queryClient.invalidateQueries({ queryKey: ["FetchClassByClassId"] });
              setPromptTeacherRemoval(false);
              setUpdatingClass(false);
              toast.success(<FormattedMessage id="ClassUpdated" />);
            } else {
              toast.warn(data);
            }
          },
          throwOnError: true,
          onError: (data) => {
            setPromptTeacherRemoval(false);
            setUpdatingClass(false);
            toast.warn(data.response.data);
          },
        }
      );
    } else {
      setClassNameInvalidSigns(false);
    }
  };

  const handleChangeProgram = (event, field) => {
    if (
      (field === "classProgram" &&
        event.value !== props.classData.data.programme) ||
      (field === "classOrientation" &&
        event.value !== props.classData.data.orientation)
    ) {
      setUpdatingClass(true);
      if (field === "classProgram") {
        values.programme = event.value;
      } else if (field === "classOrientation") {
        values.orientation = event.value;
      }
      mutateEditClass.mutate(
        {
          id: props.classData.data.id,
          name: values.name,
          school: values.school,
          programme: values.programme,
          orientation: values.orientation,
          teachers: values.teachers,
        },
        {
          onSuccess: (data) => {
            if (data && data.status > 199 && data.status < 300) {
              queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });
              queryClient.invalidateQueries({ queryKey: ["FetchClassByClassId"] });
              setPromptTeacherRemoval(false);
              setUpdatingClass(false);
              toast.success(<FormattedMessage id="ClassUpdated" />);
            } else {
              toast.warn(data);
            }
          },
          throwOnError: true,
          onError: (data) => {
            setPromptTeacherRemoval(false);
            setUpdatingClass(false);
            toast.warn(data.response.data);
          },
        }
      );
    }
  };

  const handleChangedUser = (event, user, field, userType) => {
    if (field === "name") {
      handleChangedUserName(user, event.target.value, userType);
    } else if (field === "email") {
      checkEmailOnChange(user, event.target.value, userType);
    }
  };

  const handleChangedUserName = async (user, newUserName, userType) => {
    if (newUserName !== user.name) {
      const nameCheck = checkNameLengthAndCharacters(newUserName);
      if (nameCheck !== true) {
        toast.warn(nameCheck);
      } else {
        setUpdatingUserId(user.id);
        mutateStudentName.mutate(
          {
            oldEmail: user.email,
            newEmail: user.email,
            newName: newUserName,
          },
          {
            onSuccess: (data) => {
              if (data && data.status > 199 && data.status < 300) {
                let tempClassData = props.classData.data;
                if (userType === "student") {
                  tempClassData.students.find(
                    (stud) => stud.id === user.id
                  ).name = newUserName;
                } else if (userType === "teacher") {
                  tempClassData.teachers.find(
                    (teach) => teach.id === user.id
                  ).name = newUserName;
                }
                queryClient.refetchQueries({
                  queryKey: ["FetchTeacherInformation"],
                });
                queryClient.setQueryData(
                  ["FetchClassByClassId", props.classData.data.id],
                  tempClassData
                );
                setUpdatingUserId(null);
                toast.success(<FormattedMessage id="UserUpdated" />);
              } else {
                toast.warn(data);
                setUpdatingUserId(null);
              }
            },
            throwOnError: true,
            onError: (data) => {
              setUpdatingUserId(null);
              toast.warn(data);
            },
          }
        );
      }
    }
  };

  const checkEmailOnChange = (user, newUserEmail, userType) => {
    if (user.email !== newUserEmail) {
      if (
        !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
          newUserEmail
        )
      ) {
        toast.warn(<FormattedMessage id="WrongEmailFormat" />);
      } else {
        setUpdatingUserId(user.id);
        mutateCheckStudentEmail.mutate(
          {
            oldEmail: user.email,
            newEmail: newUserEmail,
            studentName: user.name,
          },
          {
            onSuccess: (data) => {
              if (data && data.status > 199 && data.status < 300) {
                if (data.data === true) {
                  setUpdatingUserId(null);
                  toast.warn(<FormattedMessage id="EmailOccupied" />);
                } else {
                  handleChangedUserEmail(user, newUserEmail, userType);
                }
              } else {
                setUpdatingUserId(null);
                toast.warn(data);
              }
            },
            throwOnError: true,
            onError: (data) => {
              setUpdatingUserId(null);
              toast.warn(data.response.data);
            },
          }
        );
      }
    }
  };

  const handleChangedUserEmail = (user, newUserEmail, userType) => {
    mutateStudentEmail.mutate(
      {
        oldEmail: user.email,
        newEmail: newUserEmail,
        userName: user.name,
      },
      {
        onSuccess: (data) => {
          if (data && data.status > 199 && data.status < 300) {
            let tempClassData = props.classData.data;
            if (userType === "student") {
              tempClassData.students.find((stud) => stud.id === user.id).email =
                newUserEmail;
            } else if (userType === "teacher") {
              tempClassData.teachers.find(
                (teach) => teach.id === user.id
              ).email = newUserEmail;
            }
            queryClient.setQueryData(
              ["FetchClassByClassId", props.classData.data.id],
              tempClassData
            );
            setUpdatingUserId(null);
            toast.success(<FormattedMessage id="UserUpdated" />);
          } else {
            toast.warn(data);
            setUpdatingUserId(null);
          }
        },
        throwOnError: true,
        onError: (data) => {
          toast.warn(data.response.data);
          setUpdatingUserId(null);
        },
      }
    );
  };

  const compareNamePriority = (a, b, columnId) => {
    const priorityA = a.getValue(columnId).props.defaultValue;
    const priorityB = b.getValue(columnId).props.defaultValue;
    return priorityA.localeCompare(priorityB);
  };

  const compareFormattedPriority = (a, b, columnId) => {
    const valA = a.getValue(columnId);
    const valB = b.getValue(columnId);
    if (!valA) return -1;
    if (!valB) return 1;
    const priorityA = valA.props.id;
    const priorityB = valB.props.id;
    if (priorityA === priorityB) {
      return 0;
    }
    return priorityA < priorityB ? -1 : 1;
  };

  const studentTableColumns = [
    {
      id: "name",
      key: "name",
      header: () => <FormattedMessage id="Name" />,
      width: 160,
      sortingFn: compareNamePriority,
    },
    {
      id: "email",
      key: "email",
      header: () => <FormattedMessage id="Email" />,
      width: 160,
      sortingFn: compareNamePriority,
    },
    {
      id: "activatedAccount",
      key: "activatedAccount",
      header: () => <FormattedMessage id="Activated" />,
      width: 80,
      sortingFn: compareFormattedPriority,
    },
    {
      id: "disconnect",
      key: "disconnect",
      header: () => <FormattedMessage id="Edit" />,
      width: 100,
      sortingFn: compareFormattedPriority,
    },
    {
      id: "licences",
      key: "licences",
      header: () => <FormattedMessage id="Programlicense" />,
      width: 160,
      sortingFn: (a, b, columnId) =>
        a.getValue(columnId).length - b.getValue(columnId).length,
    },
    {
      id: "licenseNeeded",
      key: "licenseNeeded",
      width: 120,
      sortingFn: compareFormattedPriority,
    },
    {
      id: "aplLicense",
      key: "aplLicense",
      width: 160,
      header: () => <FormattedMessage id="AplLicense" />,
      sortingFn: compareFormattedPriority,
    },
  ];

  const studentEditField = (student, field) => {
    return (
      <CFormInput
        defaultValue={student[field]}
        onBlur={(event) => handleChangedUser(event, student, field, "student")}
      ></CFormInput>
    );
  };
  const teacherEditField = (teacher, field) => {
    if (props.teacherInfo.data.fullAccess) {
      return (
        <CFormInput
          defaultValue={teacher[field]}
          onBlur={(event) =>
            handleChangedUser(event, teacher, field, "teacher")
          }
        ></CFormInput>
      );
    } else {
      return teacher[field];
    }
  };

  const handleAddLicense = (stud) => {
    setSelectedStudentForLicense(stud);
    setLicenseModalOpen(true);
  };

  const toggleStudentLicenseModal = () => {
    setLicenseModalOpen((prev) => !prev);
  };

  const licensesBadge = (stud, showBadge) => {
    let studentHasLicense = false;
    stud.licenses.forEach((studLic) => {
      if (studLic.programLicenses.length > 0) {
        studentHasLicense = true;
      }
    });

    const renderAssignButton = () => {
      return (
        <Button
          color="primary"
          outline
          size="sm"
          disabled={!props.teacherInfo.data.purchaser}
          onClick={() => handleAddLicense(stud)}
        >
          <FormattedMessage id="AssignLicense" />
        </Button>
      );
    };

    if (studentHasLicense) {
      let licenses = [];
      if (!showBadge && stud.requiredLicenseIsMissing) {
        return renderAssignButton();
      }
      stud.licenses.forEach((studLic) => {
        studLic.programLicenses.forEach((license) => {
          var licName = license.name;
          if (licName.includes("programlicens")) {
            licName = licName.replace("programlicens", "elevlicens");
          }
          licenses.push(licName);
        });
      });
      return licenses.map((license) => (
        <Badge pill key={uuid()}>
          {license}
        </Badge>
      ));
    }
    if (!showBadge) {
      return renderAssignButton();
    }
    return "";
  };
  const studentActivationHandler = (student) => {
    if (student.activatedAccount) {
      return <FormattedMessage id="Activated" />;
    } else {
      return (
        <ButtonSendActivationMail
          user={student}
          printButtonText={false}
          leftAlign={true}
        />
      );
    }
  };

  const filterBySearch = (data, search) => {
    if (!search) return data;
    const loweredSearch = search.toLowerCase();
    return data.filter(
      (s) =>
        s.name.toLowerCase().includes(loweredSearch) ||
        s.email.toLowerCase().includes(loweredSearch)
    );
  };

  const filteredStudents = filterBySearch(
    props.classData.data.students,
    studentFilterSearch
  );

  const showAplLicenseStatus = (stud) => {
    const foundStud = allStudents.find((s) => s.id === stud.id);
    if (!foundStud) return null;

    if (!foundStud.hasValidAplLicense && !foundStud.hasAplLicense) return null;

    return (
      <div
        className={
          foundStud.hasValidAplLicense
            ? undefined
            : "text-muted text-decoration-line-through"
        }
        style={{ fontSize: "0.8rem" }}
      >
        {formatDate(foundStud.aplLicenseValidFrom, "yyyy-MM-dd") +
          " - " +
          formatDate(foundStud.aplLicenseValidTo, "yyyy-MM-dd")}
      </div>
    );
  };

  const studentTableDataSource = [];
  filteredStudents.sort(compare).forEach((stud) => {
    let student = {
      uniqueId: stud.id,
      name:
        updatingUserId === stud.id ? (
          <div style={{ height: "35px" }}>
            <PacmanLoader size={15} color={"#20a8d8"} loading={true} />
          </div>
        ) : (
          studentEditField(stud, "name")
        ),
      email: updatingUserId === stud.id ? "" : studentEditField(stud, "email"),
      activatedAccount:
        updatingUserId === stud.id ? "" : studentActivationHandler(stud),
      disconnect:
        updatingUserId === stud.id ? (
          ""
        ) : (
          <Button
            size="sm"
            outline
            color="primary"
            onClick={() => promptRemoveStudent(stud)}
          >
            <FormattedMessage id="DeleteStudent" />
          </Button>
        ),
      licences: updatingUserId === stud.id ? "" : licensesBadge(stud, true),
      licenseNeeded: stud.requiredLicenseIsMissing ? (
        licensesBadge(stud, false)
      ) : (
        <PiCheckCircle className="text-success fs-4" />
      ),
      aplLicense: showAplLicenseStatus(stud),
    };
    studentTableDataSource.push(student);
  });

  const teacherTableColumns = [
    {
      id: "name",
      key: "name",
      header: () => <FormattedMessage id="Name" />,
      width: 250,
      sortingFn: compareNamePriority,
    },
    {
      id: "email",
      key: "email",
      header: () => <FormattedMessage id="Email" />,
      width: 250,
      sortingFn: compareNamePriority,
    },
    {
      id: "activatedAccount",
      key: "activatedAccount",
      header: () => <FormattedMessage id="Activated" />,
      width: 150,
      sortingFn: compareFormattedPriority,
    },
    {
      id: "purchaser",
      key: "purchaser",
      header: () => <FormattedMessage id="Purchaser" />,
      width: 150,
      sortingFn: compareFormattedPriority,
    },

    {
      id: "fullAccessTeacher",
      key: "fullAccessTeacher",
      header: () => <FormattedMessage id="FullTeacherAccess" />,
      width: 180,
      sortingFn: compareFormattedPriority,
    },
    {
      id: "edit",
      key: "edit",
      header: () => <FormattedMessage id="Edit" />,
      width: 180,
      sortingFn: compareFormattedPriority,
    },
  ];

  const filteredTeachers = filterBySearch(
    props.classData.data.teachers,
    teacherFilterSearch
  );

  const teacherTableDataSource = [];
  filteredTeachers.sort(compare).forEach((teach) => {
    let teacher = {
      uniqueId: teach.id,
      name:
        updatingUserId === teach.id ? (
          <div style={{ height: "35px" }}>
            <PacmanLoader size={15} color={"#20a8d8"} loading={true} />
          </div>
        ) : (
          teacherEditField(teach, "name")
        ),
      email:
        updatingUserId === teach.id ? "" : teacherEditField(teach, "email"),
      activatedAccount:
        updatingUserId === teach.id ? (
          ""
        ) : teach.activatedAccount ? (
          <FormattedMessage id="Activated" />
        ) : (
          <FormattedMessage id="NotActivated" />
        ),
      purchaser:
        updatingUserId === teach.id ? (
          ""
        ) : teach.purchaser ? (
          <FormattedMessage id="IsPurchaser" />
        ) : (
          <FormattedMessage id="NotPurchaser" />
        ),
      fullAccessTeacher:
        updatingUserId === teach.id ? (
          ""
        ) : teach.fullAccess ? (
          <FormattedMessage id="true" />
        ) : (
          <FormattedMessage id="false" />
        ),
      edit:
        updatingUserId === teach.id ? (
          ""
        ) : (
          <DisconnectTeacherFromClassButton
            promptRemoveTeacher={promptRemoveTeacher}
            teacher={teach}
            classId={props.classData.data.id}
            removingTeacher={removingTeacher}
          />
        ),
    };
    teacherTableDataSource.push(teacher);
  });

  const searchList = () => {
    return classCoursesData.data.map((course, i) => (
      <Row key={i}>
        <Col>{course.name}</Col>
      </Row>
    ));
  };

  const toggleCoursesDropdown = () => {
    setShowCourses((prevstate) => !prevstate);
  };

  const programSelectOptions = [];
  props.teacherInfo.data.programmes.forEach((program) => {
    let prog = { value: program, label: program.name };
    programSelectOptions.push(prog);
  });

  const currentProgramOption = {
    value: props.classData.data.programme ? props.classData.data.programme : "",
    label: props.classData.data.programme
      ? props.classData.data.programme.name
      : "",
  };

  const orientationSelectOptions = [];
  props.schoolInfo.data.orientations.forEach((orientation) => {
    let orient = { value: orientation, label: orientation.name };
    orientationSelectOptions.push(orient);
  });

  const currentOrientation = { value: "", label: "" };
  if (props.classData.data.orientation != null) {
    currentOrientation.value = props.classData.data.orientation;
    currentOrientation.label = props.classData.data.orientation.name;
  }

  const teacherSelectOptions = () => {
    let teacherSelectOptions = [];
    let addNew = {
      value: "addNewTeacher",
      label: <FormattedMessage id="AddNewTeacher" />,
    };

    props.schoolInfo.data.teachers.forEach((teacher) => {
      let teach = { value: teacher, label: teacher.name };
      let teacherIsTeacherInClass = false;
      props.classData.data.teachers.forEach((teacherInClass) => {
        if (teacherInClass.id === teach.value.id) {
          teacherIsTeacherInClass = true;
        }
      });
      if (!teacherIsTeacherInClass) {
        teacherSelectOptions.push(teach);
      }
    });
    teacherSelectOptions.sort((a, b) =>
      a.label > b.label ? 1 : b.label > a.label ? -1 : 0
    );

    if (teacherSelectOptions.length > 0) {
      teacherSelectOptions.splice(0, 0, teacherSelectOptions.pop());
    }
    teacherSelectOptions.unshift(addNew);
    return teacherSelectOptions;
  };

  const getStudentIds = () => {
    let listOfIds = [];
    props.classData.data.students.forEach((stud) => {
      listOfIds.push(stud.id);
    });
    return listOfIds;
  };

  return (
    <div className="animated fadeIn">
      <Col lg={12}>
        <Card body>
          <CardTitle>
            <FormattedMessage id="ClassInformation" />
            {updatingClass ? (
              <div style={{ float: "right" }}>
                {" "}
                <div style={{ marginTop: "-11%", marginBottom: "22%" }}>
                  <PacmanLoader color={"#20a8d8"} size={15} loading={true} />
                </div>
                <FormattedMessage id="Updating" />
              </div>
            ) : (
              ""
            )}
            <hr />
          </CardTitle>

          <CRow>
            <CCol sm="6">
              <CForm key={props.classData.data.id} action="" method="post">
                <CFormLabel htmlFor="className">
                  <FormattedMessage id="Class" />:
                </CFormLabel>
                <CFormInput
                  type="text"
                  id="className"
                  name="className"
                  onBlur={handleChangedClassName}
                  defaultValue={props.classData.data.name}
                  placeholder={props.classData.data.name}
                  autoComplete="false"
                />
                {classNameInvalidSigns ? (
                  <span className="ms-2 text-danger">
                    <FontAwesomeIcon icon="exclamation" className="me-1" />
                    <FormattedMessage id="InvalidClassName" />
                  </span>
                ) : (
                  ""
                )}
                <CFormLabel htmlFor="classProgram">
                  <FormattedMessage id="Program" />:
                </CFormLabel>
                <Select
                  disabled={!props.fullAccessTeacher}
                  menuPortalTarget={document.body}
                  menuPosition={"fixed"}
                  name="classProgram"
                  options={programSelectOptions}
                  defaultValue={currentProgramOption}
                  onChange={(event) =>
                    handleChangeProgram(event, "classProgram")
                  }
                />
                <CFormLabel htmlFor="classProgram">
                  <FormattedMessage id="Orientation" />:
                </CFormLabel>
                <Select
                  disabled={!props.fullAccessTeacher}
                  menuPortalTarget={document.body}
                  name="classOrientation"
                  menuPosition={"fixed"}
                  options={orientationSelectOptions}
                  defaultValue={currentOrientation}
                  onChange={(event) =>
                    handleChangeProgram(event, "classOrientation")
                  }
                />
              </CForm>
            </CCol>
          </CRow>
        </Card>
      </Col>

      <FocusControl
        targetSettings={fc.targetSettings}
        deviantStudents={fc.deviantStudents}
        onDelete={fc.handleDeleteFocusControl}
        deletingTarget={fc.deletingTarget}
        deviationSource="Class"
        inheritedSourceId="InheritedFromProgram"
        onSaveTarget={fc.handleSaveFocusTarget}
        savingTarget={fc.savingTarget}
      />

      <ModuleLocker
        courses={classCoursesData.data}
        level="Class"
        currentLevelId={props.classData.data.id}
        students={getStudentIds()}
      />

      <FinalExamUnlockerContainer
        programId={props.classData.data.programme.id}
        courses={classCoursesData.data}
        searchList={searchList}
        placeHolder={props.placeHolder}
        toggleCoursesDropdown={toggleCoursesDropdown}
        showCourses={showCourses}
        classId={props.classData.data.id}
        level="Class"
      />

      <Col lg={12}>
        <Card body>
          <CardTitle>
            <FormattedMessage id="Students" />
            <hr className="pb-0 mb-0" />
          </CardTitle>
          <CardSubtitle>
            <div className="d-flex align-items-center flex-wrap m-1">
              <div className="m-2">
                <FontAwesomeIcon
                  icon="search"
                  className="position-absolute ms-3"
                  style={{ marginTop: "10px" }}
                />
                <Input
                  id="studentSearchInput"
                  className="rounded-4 ps-5"
                  onChange={(e) => setStudentFilterSearch(e.target.value)}
                  placeholder="Namn eller e-post"
                />
              </div>
              <Button
                size="sm"
                outline
                color="primary"
                className="my-1 m-y-md-0 me-md-1"
                onClick={() => openAddNewStudentToClassModal()}
              >
                <FormattedMessage id="AddNewStudentToClass" />
              </Button>
              <Button
                size="sm"
                outline
                color="primary"
                disabled={!props.fullAccessTeacher}
                className="my-1 m-y-md-0 mx-md-1"
                onClick={() => openAddStudentToClassModal()}
              >
                <FormattedMessage id="MoveExistingStudentsToClass" />
              </Button>
              <Button
                size="sm"
                outline
                color="primary"
                disabled={!props.fullAccessTeacher}
                className="my-1 m-y-md-0 mx-md-1"
                onClick={() => toggleImportStudentsModal()}
              >
                <FormattedMessage id="ImportFromXlsx" />
              </Button>
            </div>
          </CardSubtitle>

          <DataGrid
            columns={studentTableColumns}
            rows={studentTableDataSource}
          />
        </Card>
      </Col>

      <Col lg={12}>
        <Card body>
          <CardTitle>
            <FormattedMessage id="Teachers" />
            <hr className="pb-0 mb-0" />
          </CardTitle>
          <CardSubtitle>
            <div className="d-flex align-items-center flex-wrap m-1">
              <div className="m-2">
                <FontAwesomeIcon
                  icon="search"
                  className="position-absolute ms-3"
                  style={{ marginTop: "10px" }}
                />
                <Input
                  id="teacherSearchInput"
                  className="rounded-4 ps-5"
                  onChange={(e) => setTeacherFilterSearch(e.target.value)}
                  placeholder="Namn eller e-post"
                />
              </div>
              <div style={{ width: "250px" }}>
                <Select
                  disabled={!props.fullAccessTeacher}
                  menuPortalTarget={document.body}
                  menuPosition={"fixed"}
                  options={teacherSelectOptions()}
                  placeholder={<FormattedMessage id="AddTeacher" />}
                  onChange={handleAddTeacherSelect}
                />
              </div>
            </div>
          </CardSubtitle>
          <DataGrid
            columns={teacherTableColumns}
            rows={teacherTableDataSource}
          />
        </Card>
      </Col>

      <AddNewTeacherToClass
        isOpen={addNewTeacherModalOpen}
        toggle={toggleNewTeacherModal}
        onSubmit={handleAddNewTeacher}
        fullAccessTeacher={props.fullAccessTeacher}
        purchaserTeacher={props.teacherInfo.data.purchaser}
      />
      <ConfirmDialog
        isOpen={promptTeacherRemoval}
        toggle={() => setPromptTeacherRemoval(!promptTeacherRemoval)}
        title={<FormattedMessage id="AreYouSure" />}
        message={
          <FormattedMessage
            values={{ teacherName: teacherForRemoval.name }}
            id="WillBeRemovedFromClass"
          />
        }
        onProceed={handleRemoveTeacher}
        danger={true}
      />
      <ConfirmDialog
        isOpen={promptStudentRemoval}
        toggle={() => setPromptStudentRemoval(!promptStudentRemoval)}
        title={<FormattedMessage id="AreYouSure" />}
        message={
          <FormattedMessage
            values={{ studentName: studentForRemoval.name }}
            id="StudentWillBeDeleted"
          />
        }
        onProceed={handleDeleteStudent}
        danger={true}
      />
      <StudentLicenseManagemenModal
        isOpen={licenseModalOpen}
        toggle={toggleStudentLicenseModal}
        preselectedStudent={selectedStudentForLicense}
        className={props.classData.data.name}
      />
      <CreateStudentInClass
        isOpen={addNewStudentToClassModal}
        classId={props.classData.data.id}
        addNewStudentToClassModal={addNewStudentToClassModal}
        toggleAndAddLicense={(stud) => closeAddNewStudentAndAddLicense(stud)}
        toggle={() => toggleModals("addNewStudentToClass")}
        purchaserTeacher={props.teacherInfo.data.purchaser}
      />

      <MoveExistingStudentToClassModal
        classId={props.classData.data.id}
        isOpen={addStudentToClassModal}
        toggle={() => toggleModals("addStudentToClass")}
        students={allStudents}
        loadStudents={reloadClass}
        existingStudents={studentsInClass}
      />

      <UpdateClassModal
        schoolId={props.teacherInfo.data.customer.id}
        isOpen={updateClassModal}
        toggle={() => toggleModals("updateClass")}
        classToUpdate={props.classData.data}
        dismissModal={() => toggleModals("updateClass")}
        programmeSelection={props.schoolInfo.data.programmes}
        teacherSelection={props.schoolInfo.data.teachers}
        orientationSelection={props.schoolInfo.data.orientations}
        onProceed={reload}
      />
      <ImportStudentsToClassModal
        preSelectedClass={true}
        preSelectedClassId={props.classData.data.id}
        reloadClass={reloadClass}
        handleHide={toggleImportStudentsModal}
        studentClasses={[]}
        isOpen={importStudentsModalOpen}
      />
    </div>
  );
}
